import { Box, Button, MenuItem, Select, Stack } from "@mui/material";
import { useState } from "react";
import { ApiError, type TokenResponse, requestToken } from "../../data/api";
import { useConsole } from "../../state/state";
import { Empty, Failure, Field, Hint, PageHeader, Section } from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import { TokenView } from "./TokenView";

// ---
// Token inspector
//
// The audiences offered are the selected tenant's own, read from its catalogue, plus the control
// plane — which belongs to no tenant and every signed-in subject can reach. They used to be three
// strings in the console's source, which made one customer's APIs the only ones it could show.
//
// Whether a project is required is asked of the audience rather than remembered beside it: an API
// that requires the dimension refuses a token without one.
// ---

export function TokenInspector(): React.JSX.Element {
  const tokens = useTokens();
  const { tenant, tenants, projects, registeredApis, bootstrap } = useConsole();
  const [audience, setAudience] = useState("");
  const [project, setProject] = useState("");
  const [token, setToken] = useState<TokenResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tenantName = tenants.find((each) => each.id === tenant)?.name ?? tenant;

  const audiences =
    bootstrap === null
      ? []
      : [
          {
            resource: bootstrap.controlPlane,
            label: "Control plane",
            requiredDims: [] as string[],
          },
          ...registeredApis
            .filter((api) => api.resource !== bootstrap.controlPlane)
            .map((api) => ({
              resource: api.resource,
              label: api.resource.replace(/^https:\/\//, ""),
              requiredDims: api.requiredDims,
            })),
        ];

  const chosen = audience !== "" ? audience : (audiences[0]?.resource ?? "");
  const needsProject =
    audiences.find((each) => each.resource === chosen)?.requiredDims.includes("project") ?? false;
  const chosenProject = project !== "" ? project : (projects[0]?.id ?? "");

  async function mint(): Promise<void> {
    setBusy(true);
    setError(null);
    setToken(null);
    try {
      setToken(
        await requestToken({
          resource: chosen,
          tenantId: tenant,
          ...(needsProject ? { projectId: chosenProject } : {}),
        }),
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? `${caught.code}: ${caught.message}` : String(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="Token inspector" />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          gap: 4,
          alignItems: "start",
        }}
      >
        <Section title="Request a token">
          <Stack spacing={4}>
            <Field label="Tenant">
              <Select size="small" value={tenant} disabled>
                <MenuItem value={tenant}>{tenantName}</MenuItem>
              </Select>
            </Field>
            <Field label="Audience">
              <Select
                size="small"
                value={chosen}
                onChange={(event) => setAudience(event.target.value)}
              >
                {audiences.map((each) => (
                  <MenuItem key={each.resource} value={each.resource}>
                    {each.label}
                  </MenuItem>
                ))}
              </Select>
            </Field>
            {needsProject && (
              <Field label="Project">
                <Select
                  size="small"
                  value={chosenProject}
                  displayEmpty
                  onChange={(event) => setProject(event.target.value)}
                >
                  {projects.length === 0 && <MenuItem value="">No accessible projects</MenuItem>}
                  {projects.map((each) => (
                    <MenuItem key={each.id} value={each.id}>
                      {each.name}
                    </MenuItem>
                  ))}
                </Select>
              </Field>
            )}
            <Button
              variant="contained"
              onClick={() => void mint()}
              disabled={busy || tenant === "" || (needsProject && project === "")}
            >
              {busy ? "Requesting…" : "Exchange session"}
            </Button>
          </Stack>
          <Hint>The tenant is the one selected in the sidebar.</Hint>
          <Failure error={error} />
        </Section>

        <Section title="Token">
          {token === null ? (
            <Empty>No token yet. Exchange the session to mint one.</Empty>
          ) : (
            <TokenView token={token} />
          )}
        </Section>
      </Box>
    </>
  );
}
